import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { GestationalAgeComponent } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/GestationalAgeComponent";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";
import { QuebecObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Quebec/QuebecObstetricalFileFieldLimits";
import { Ultrasound } from "@/Patient/Models/ObstetricalFile/Quebec/PageThree/Ultrasound";

class UltrasoundTableProps {
    public ultrasounds: Ultrasound[];
    public onChange: (ultrasounds: Ultrasound[], shouldHandleFormUpdate: boolean, estimatedDateOfConfinementCalculationChanged: boolean) => void;
    public readonly: boolean;
    public controlRenderer: ObstetricalFileControlRenderer;
}

class UltrasoundTableState {
    public ultrasounds: Ultrasound[];

    constructor() {
        this.ultrasounds = [];
    }
}

class UltrasoundTable extends BaseReactComponent<UltrasoundTableProps, UltrasoundTableState> {
    constructor(props: UltrasoundTableProps) {
        super(props);
        this.state = new UltrasoundTableState();
    }

    public componentDidMount(): void {
        this.mutateState(s => s.ultrasounds = this.manageEmptyRow(this.props.ultrasounds));
    }

    public componentDidUpdate(prevProps: Readonly<UltrasoundTableProps>): void {
        if (this.props?.ultrasounds !== prevProps?.ultrasounds || !this.state?.ultrasounds?.any())
            this.mutateState(s => s.ultrasounds = this.manageEmptyRow(this.props.ultrasounds));
    }

    public render(): JSX.Element {
        return (
            <div className="flex-column ultrasound-table">
                <div className="ultrasound-table-header-row ultrasound-table-row">
                    <div className="col-md-2" />
                    <div className="col-md-2">
                        <h5 className="title">{cacheStore.GetString("ObstetricalFile_UltrasoundLabel")}</h5>
                        <div className="field-label">{cacheStore.GetString("DateLabel")}</div>
                    </div>
                    <div className="col-md-2"><div className="field-label">{cacheStore.GetString("ObstetricalFile_ClinicalAgeLabel")}</div></div>
                    <div className="col-md-2"><div className="field-label">{cacheStore.GetString("ObstetricalFile_UltrasoundAgeLabel")}</div></div>
                    <div className="col-md-2"><div className="field-label">{cacheStore.GetString("ObstetricalFile_PlacentaLabel")}</div></div>
                    <div className="col-md-2" />
                </div>
                {this.state?.ultrasounds?.map((ultrasound, index) => (
                    <UltrasoundTableRow
                        ultrasound={ultrasound}
                        readonly={this.props.readonly}
                        onChange={(data, shouldHandleFormUpdate, estimatedDateOfConfinementCalculationChanged) => this.onRowChange(index, data, shouldHandleFormUpdate, estimatedDateOfConfinementCalculationChanged)}
                        controlRenderer={this.props.controlRenderer}
                        index={index}
                        // We can't use ultrasound.id because it's regenerated each time the form values change, which
                        // removes focus on the current input. Using the index is safe in this specific case because we
                        // can't reorder rows and can only delete from the bottom, so two current rows will never swap
                        // indexes.
                        key={`tableRow${index}`}
                    />
                ))}
            </div>
        );
    }

    private manageEmptyRow(ultrasounds: Ultrasound[]): Ultrasound[] {
        if (this.listIsEmpty() || (this.listHasLessThanFourUltrasounds() && this.lastUltrasoundHasData(this.props.ultrasounds)))
            return [...(ultrasounds || []), new Ultrasound()];

        return [...(ultrasounds || [])];
    }

    private listIsEmpty(): boolean {
        return !this.props.ultrasounds?.length;
    }

    private listHasLessThanFourUltrasounds(): boolean {
        return (this.props.ultrasounds?.length ?? 0) < 4;
    }

    private lastUltrasoundHasData(ultrasounds: Ultrasound[]): boolean {
        if (!ultrasounds?.length)
            return false;

        const lastUltrasound = ultrasounds[ultrasounds.length - 1];
        return !!lastUltrasound.Date
            || !!lastUltrasound.ClinicalAge?.hasValue()
            || !!lastUltrasound.Placenta0
            || !!lastUltrasound.Placenta1
            || !!lastUltrasound.UltrasoundAge?.hasValue();
    }

    private onRowChange(rowIndex: number, data: Ultrasound, shouldHandleFormUpdate: boolean, estimatedDateOfConfinementCalculationChanged: boolean): void {
        const newData = [...(this.props.ultrasounds ?? [])];

        if (!data.Date || !data.UltrasoundAge?.hasValue())
            data.UseForDating = false;

        if (data.UseForDating)
            newData.forEach(p => p.UseForDating = false);

        newData[rowIndex] = data;

        if (!this.lastUltrasoundHasData(newData))
            newData.pop();

        this.props.onChange(newData, shouldHandleFormUpdate, estimatedDateOfConfinementCalculationChanged);
    }
}

class UltrasoundRowProps {
    public index: number;
    public ultrasound: Ultrasound;
    public onChange: (data: Ultrasound, shouldHandleFormUpdate?: boolean, estimatedDateOfConfinementCalculationChanged?: boolean) => void;
    public readonly: boolean;
    public controlRenderer: ObstetricalFileControlRenderer;
}

class UltrasoundTableRow extends BaseReactComponent<UltrasoundRowProps> {
    public render(): JSX.Element {
        const isUseForDatingDisabled = this.props.readonly || !this.props.ultrasound.Date || !this.props.ultrasound.UltrasoundAge?.hasValue();

        return (
            <div className="ultrasound-table-row">

                <div className="col-md-2">
                    <div className="flex-row">
                        {this.props.controlRenderer.renderRadioButtonWithLabel(
                            `useForDating${this.props.index}`,
                            "useForDating",
                            cacheStore.GetString("ObstetricalFile_UseForDatingLabel"),
                            this.props.ultrasound.UseForDating,
                            isUseForDatingDisabled,
                            event => this.props.onChange({ ...this.props.ultrasound, UseForDating: event.currentTarget.checked }, false, true),
                            false,
                            "use-for-dating"
                        )}
                    </div>
                </div>

                <div className="col-md-2">
                    {this.props.controlRenderer.renderDateSelector({
                        value: this.props.ultrasound.Date,
                        onChange: value => this.props.onChange({ ...this.props.ultrasound, Date: value }, true, true),
                        id: `UltrasoundDate${this.props.index}`
                    })}
                </div>

                <div className="col-md-2">
                    <GestationalAgeComponent
                        id={`UltrasoundClinicalAge_${this.props.index}`}
                        gestationalAge={this.props.ultrasound.ClinicalAge}
                        readonly={this.props.readonly}
                        onChange={value => this.props.onChange({ ...this.props.ultrasound, ClinicalAge: value })}
                        controlRenderer={this.props.controlRenderer}
                    />
                </div>
                <div className="col-md-2">
                    <GestationalAgeComponent
                        id={`UltrasoundAge_${this.props.index}`}
                        gestationalAge={this.props.ultrasound.UltrasoundAge}
                        readonly={this.props.readonly}
                        onChange={value => this.props.onChange({ ...this.props.ultrasound, UltrasoundAge: value }, false, true)}
                        controlRenderer={this.props.controlRenderer}
                    />
                </div>

                <div className="col-md-2">
                    {this.props.controlRenderer.renderTextbox({
                        value: this.props.ultrasound.Placenta0,
                        onChange: placenta0 => this.props.onChange({ ...this.props.ultrasound, Placenta0: placenta0 }),
                        maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsUltrasoundPlacenta0.MaxChars
                    })}
                </div>

                <div className="col-md-2">
                    {this.props.controlRenderer.renderTextbox({
                        value: this.props.ultrasound.Placenta1,
                        onChange: placenta1 => this.props.onChange({ ...this.props.ultrasound, Placenta1: placenta1 }),
                        maxLength: QuebecObstetricalFileFieldLimits.Page3.ComplementaryExamsUltrasoundPlacenta1.MaxChars
                    })}
                </div>

            </div>
        );
    }
}

export { UltrasoundTable };
